import { useEffect, useMemo, useRef, useState } from 'react';
import { CaretDown, MagnifyingGlass } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { useAsyncTeamSearch, type TeamLite } from '@/hooks/useAsyncTeamSearch';

interface Props {
  slug: string;
  // Currently-selected team. Null/empty when nothing is picked. If only
  // an ID is known (e.g. seeded from a record's team_id), pass
  // `{ id, name: '', color: '' }` and the picker resolves the label.
  value: TeamLite | null;
  onChange: (team: TeamLite | null) => void;
  placeholder: string;
  searchPlaceholder: string;
  emptyLabel: string;
}

// TeamPicker — click-to-open popover with a server-side searchable team
// list. Single-select; clicking a team commits and closes. Shared between
// ActivityFormModal and TemplateSlotFormModal.
//
// Data fetching lives inside the picker via useAsyncTeamSearch — callers
// pass slug/value instead of pre-loading a teams[] array. This is what
// makes the picker correct for orgs with >25 teams; previously the parent
// passed only the first page from listTeams and search ran client-side
// over that subset.
export function TeamPicker({
  slug,
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  emptyLabel,
}: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Only fetch while the popover is open. The selected team's label is
  // resolved independently via resolveIds so the closed trigger still
  // shows the right name even before the user has interacted.
  const resolveIds = useMemo(() => (value?.id ? [value.id] : []), [value?.id]);
  const { search, setSearch, teams, truncated, total, isLoading, resolved } =
    useAsyncTeamSearch(slug, { enabled: open || !!value?.id, resolveIds });

  // If the parent passed a thin value (id only, name empty) and we've
  // resolved the label, lift it back so any subsequent re-renders show
  // it consistently — and so callers reading value.name (e.g. the
  // template-slot magic-wand) see the resolved label.
  useEffect(() => {
    if (!value?.id) return;
    if (value.name) return;
    const hit = resolved.get(value.id);
    if (hit && hit.name) onChange(hit);
  }, [value, resolved, onChange]);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      setSearch('');
    }
    // setSearch is stable; deps lint is happy without it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const selectedLabel = value?.name || resolved.get(value?.id ?? '')?.name || '';

  return (
    <div ref={wrapRef} className="relative mt-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex h-10 w-full items-center justify-between gap-2 rounded-xl border border-border bg-background px-3 text-left text-sm transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          value ? 'text-foreground' : 'text-muted-foreground',
          open && 'border-primary',
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="truncate">{selectedLabel || placeholder}</span>
        <CaretDown weight="bold" className="h-4 w-4 shrink-0 text-muted-foreground" />
      </button>
      {open && (
        <div
          role="listbox"
          className="absolute left-0 right-0 z-50 mt-1 flex max-h-72 flex-col overflow-hidden rounded-xl border border-border bg-background shadow-lg"
        >
          <div className="flex items-center gap-2 border-b border-border px-3 py-2">
            <MagnifyingGlass weight="bold" className="h-4 w-4 text-muted-foreground" />
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
          <div className="overflow-y-auto py-1">
            {teams.length === 0 ? (
              <p className="px-3 py-2 text-sm text-muted-foreground">
                {isLoading ? t('common.loading') : emptyLabel}
              </p>
            ) : (
              teams.map((tm) => {
                const active = tm.id === value?.id;
                return (
                  <button
                    key={tm.id}
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => {
                      onChange(tm);
                      setOpen(false);
                    }}
                    className={cn(
                      'block w-full truncate px-3 py-2 text-left text-sm transition-colors',
                      active
                        ? 'bg-primary/15 font-semibold text-foreground'
                        : 'text-foreground hover:bg-muted',
                    )}
                  >
                    {tm.name}
                  </button>
                );
              })
            )}
          </div>
          {truncated && (
            <div className="border-t border-border bg-muted/40 px-3 py-1.5 text-xs text-foreground/60">
              {t('schedule.filter.searchMoreHint', { count: total - teams.length })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
